import { Area } from 'react-easy-crop/types';
import { ICroppedImage } from 'models/profile-picture/ICroppedImage';

const MAX_WIDTH: number = process.env.PHOTO_MAX_WIDTH ? +process.env.PHOTO_MAX_WIDTH : 400;

/**
 * Reads a photo from file and returns an image canvas element
 *
 * @param photo
 */
const readPhoto = async (photo: File): Promise<HTMLCanvasElement> => {
    const canvas = document.createElement('canvas');
    const img = document.createElement('img');
    // create img element from File object
    img.src = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.addEventListener('load', () => resolve(new Promise((finalResolve) => {
            if (typeof reader.result === 'string') {
                finalResolve(reader.result);
            } else {
                finalResolve('');
            }
        })), false);
        reader.readAsDataURL(photo);
    });

    await new Promise((resolve) => {
        img.onload = resolve;
    });

    // draw image in canvas element
    canvas.width = img.width;
    canvas.height = img.height;
    canvas?.getContext('2d')?.drawImage(img, 0, 0, canvas.width, canvas.height);

    return canvas;
};

/**
 * Scales image base64 string
 * @param canvas - image canvas
 * @param scale - scale factor 1.0 = 100%
 */
const scaleCanvas = (canvas: HTMLCanvasElement, scale: number) => {
    const scaledCanvas = document.createElement('canvas');
    scaledCanvas.width = canvas.width * scale;
    scaledCanvas.height = canvas.height * scale;

    scaledCanvas?.getContext('2d')?.drawImage(canvas, 0, 0, scaledCanvas.width, scaledCanvas.height);

    return scaledCanvas;
};

/**
 * Resize photo down to a more manageable size. max set up above
 * @param photo
 */
export const optimizePhoto = async (photo: File): Promise<string> => {
    let canvas = await readPhoto(photo);

    while (canvas.width >= 2 * MAX_WIDTH) {
        canvas = scaleCanvas(canvas, .5);
    }

    if (canvas.width > MAX_WIDTH) {
        canvas = scaleCanvas(canvas, MAX_WIDTH / canvas.width);
    }

    return new Promise((resolve) => {
        // canvas.toBlob(resolve, 'image/jpeg', QUALITY);
        resolve(canvas.toDataURL('image/jpeg'));
    });
};

const createImage = ((url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.addEventListener('load', () => resolve(image));
        image.addEventListener('error', (error) => reject(error));
        image.setAttribute('crossOrigin', 'anonymous'); // needed to avoid cross-origin issues on CodeSandbox
        image.src = url;
    }));

const getRadianAngle = (degreeValue: number) => {
    return (degreeValue * Math.PI) / 180;
};

/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 * @param {File} imageSrc - Image File url
 * @param {Object} pixelCrop - pixelCrop Object provided by react-easy-crop
 * @param {number} rotation - optional rotation parameter
 */
export const getCroppedImg = async (imageSrc: string, pixelCrop: Area, rotation: number = 0): Promise<ICroppedImage | null> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) return null;

    const maxSize = Math.max(image.width, image.height);
    const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

    // set each dimensions to double largest dimension to allow for a safe area for the
    // image to rotate in without being clipped by canvas context
    canvas.width = safeArea;
    canvas.height = safeArea;

    // translate canvas context to a central location on image to allow rotating around the center.
    ctx.translate(safeArea / 2, safeArea / 2);
    ctx.rotate(getRadianAngle(rotation));
    ctx.translate(-safeArea / 2, -safeArea / 2);

    // draw rotated image and store data.
    ctx.drawImage(
        image,
        safeArea / 2 - image.width * 0.5,
        safeArea / 2 - image.height * 0.5
    );
    const data = ctx.getImageData(0, 0, safeArea, safeArea);

    // set canvas width to final desired crop size - this will clear existing context
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // paste generated rotate image with correct offsets for x,y crop values.
    ctx.putImageData(
        data,
        Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
        Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y)
    );

    // As Base64 string
    const base64 = canvas.toDataURL('image/jpeg');
    // for iPhone safari 8, base64 string must be divisible by 4 or it doesn't display! (why tho)
    // while (base64.length % 3 !== 0) {
    //     base64 += '=';
    // }
    // As a blob
    const blob = await new Promise((resolve) => {
        canvas.toBlob((file) => {
            resolve(file);
        }, 'image/jpeg');
    });
    return {base64, blob: blob as Blob};
};
